import { create } from 'zustand';
import { etDateKey, isDuringMarketHours } from '../Utils/marketHours';
import { seriesGapRangesForDate } from '../Utils/seriesPresentation';

const KEY = 'spx';

export const createGreeksState = (set, get) => ({
  // --- SPX-only state buckets ---
  greekExposures: { [KEY]: [] },
  topOptions: { [KEY]: [] },
  dynamicLevels: { [KEY]: [] },
  gexTrendStrength: { [KEY]: [] },
  adaptiveTopOptions: { [KEY]: [] },
  adaptiveDynamicLevels: { [KEY]: [] },
  connectionStatus: 'unknown',
  lastObservationAt: null,
  disconnectedAt: null,
  liveGapRanges: [],

  setConnectionStatus: (connectionStatus, at = Date.now()) => set((state) => ({
    connectionStatus,
    disconnectedAt: ['disconnected', 'closed'].includes(connectionStatus)
      ? state.disconnectedAt ?? at
      : state.disconnectedAt,
  })),

  recordObservation: (at = Date.now()) => set((state) => {
    const observationAt = Number(at);
    if (!Number.isFinite(observationAt)) return state;
    const from = Number.isFinite(state.disconnectedAt)
      ? Math.floor(state.disconnectedAt / 60_000) * 60_000 + 60_000
      : null;
    const to = Math.floor(observationAt / 60_000) * 60_000;
    const gap = Number.isFinite(from)
      && to > from
      && etDateKey(from) === etDateKey(to)
      && isDuringMarketHours(from)
      && isDuringMarketHours(to)
      ? [{ from, to, source: 'greeks:feedObservation' }]
      : [];
    const retainedGapRanges = seriesGapRangesForDate(state.liveGapRanges, observationAt);
    const liveGapRanges = gap.length || retainedGapRanges.length !== state.liveGapRanges.length
      ? [...retainedGapRanges, ...gap]
      : state.liveGapRanges;
    return {
      connectionStatus: 'connected',
      lastObservationAt: observationAt,
      disconnectedAt: null,
      liveGapRanges,
    };
  }),

  // Last good values to smooth spikes / zero resets
  lastValidData: {
    [KEY]: {
      greekExposures: {},
      gexTrendStrength: { trend_type: null, strength_value: null },
      topOptions: [],
      dynamicLevels: [],
    },
  },

  // ---- Actions (index param is accepted but ignored; always uses 'spx') ----
  setInitialData: (_indexIgnored, data = {}) => {
    const key = KEY;

    const lastValidData = {
      greekExposures: {},
      topOptions: data.top_options || [],
      dynamicLevels: data.dynamic_levels || [],
      gexTrendStrength: { trend_type: null, strength_value: null },
    };

    const filteredGreekExposures = (data.greek_exposures || []).filter(
      (exp) =>
        exp.calls_total !== 0 &&
        exp.puts_total !== 0 &&
        exp.net_exposure !== 0
    );

    filteredGreekExposures.forEach((exp) => {
      lastValidData.greekExposures[exp.greek_type] = {
        calls_total: exp.calls_total,
        puts_total: exp.puts_total,
        net_exposure: exp.net_exposure,
      };
    });

    // Accept either gex_trend (array) or nothing
    const formattedGexTrend = (data.gex_trend || []).map((item) => {
      let strengthValue = item.strength_value;
      let trendType = item.trend_type;

      // Revert obvious spikes (0/1) to last valid
      if (item.strength_value === 1 || item.strength_value === 0) {
        strengthValue = lastValidData.gexTrendStrength.strength_value || 0;
        trendType =
          lastValidData.gexTrendStrength.trend_type ?? item.trend_type;
      } else {
        lastValidData.gexTrendStrength.trend_type = item.trend_type;
        lastValidData.gexTrendStrength.strength_value = item.strength_value;
        trendType = item.trend_type;
      }

      return {
        timestamp: item.timestamp,
        formatted_date: item.formatted_date,
        trend_type: trendType,
        value: strengthValue,
      };
    });

    set((state) => ({
      ...state,
      greekExposures: { ...state.greekExposures, [key]: filteredGreekExposures },
      topOptions: { ...state.topOptions, [key]: data.top_options || [] },
      dynamicLevels: { ...state.dynamicLevels, [key]: data.dynamic_levels || [] },
      gexTrendStrength: { ...state.gexTrendStrength, [key]: formattedGexTrend },
      adaptiveTopOptions: {
        ...state.adaptiveTopOptions,
        [key]: data.top_options || [],
      },
      adaptiveDynamicLevels: {
        ...state.adaptiveDynamicLevels,
        [key]: data.dynamic_levels || [],
      },
      lastValidData: { ...state.lastValidData, [key]: lastValidData },
    }));
  },

  updateGreekExposures: (_indexIgnored, updates = {}) => {
    const key = KEY;
    set((state) => {
      const hasZeroValues = Object.values(updates).some(
        (data) =>
          data.calls_total === 0 ||
          data.puts_total === 0 ||
          data.net_exposure === 0
      );

      const updateArray = Object.entries(updates).map(
        ([greek_type, data]) => {
          if (
            hasZeroValues &&
            state.lastValidData[key].greekExposures[greek_type]
          ) {
            return {
              greek_type,
              timestamp: data.timestamp,
              formatted_date: data.formatted_date,
              ...state.lastValidData[key].greekExposures[greek_type],
            };
          }

          state.lastValidData[key].greekExposures[greek_type] = {
            calls_total: data.calls_total,
            puts_total: data.puts_total,
            net_exposure: data.net_exposure,
          };

          return { greek_type, ...data };
        }
      );

      return {
        ...state,
        greekExposures: {
          ...state.greekExposures,
          [key]: [...(state.greekExposures[key] || []), ...updateArray],
        },
        lastValidData: { ...state.lastValidData },
      };
    });
  },

  updateGexTrendStrength: (_indexIgnored, update) => {
    if (!update?.updateData) return;
    const key = KEY;

    set((state) => {
      let strengthValue = update.updateData.strength_value;
      let trendType = update.updateData.trend_type;

      const prev = state.lastValidData[key].gexTrendStrength;
      const series = state.gexTrendStrength[key] || [];

      // Spike / flip guard
      if (
        update.updateData.strength_value === 1 ||
        (update.updateData.trend_type !== prev.trend_type &&
          series.length > 0 &&
          update.updateData.strength_value >= 0.95)
      ) {
        strengthValue = prev.strength_value;
        trendType = prev.trend_type;
      } else {
        state.lastValidData[key].gexTrendStrength = {
          trend_type: trendType,
          strength_value: strengthValue,
        };
      }

      const formattedUpdate = {
        timestamp: update.updateData.timestamp,
        formatted_date: update.updateData.formatted_date,
        trend_type: trendType,
        value: strengthValue,
      };

      return {
        ...state,
        gexTrendStrength: {
          ...state.gexTrendStrength,
          [key]: [...series, formattedUpdate],
        },
        lastValidData: { ...state.lastValidData },
      };
    });
  },

  updateTopOptions: (_indexIgnored, updates = []) => {
    const key = KEY;
    set((state) => {
      const updatesWithStrike = (updates || []).map((u) => ({
        ...u,
        strike_price: u.strike_price ?? u.strike,
      }));

      const recentExposures = (state.greekExposures[key] || []).slice(-4);
      const hasRecentBadData =
        recentExposures.some(
          (exp) =>
            exp.calls_total === 0 ||
            exp.puts_total === 0 ||
            exp.net_exposure === 0
        ) && (state.lastValidData?.[key]?.topOptions?.length || 0) > 0;

      const dataToUse = hasRecentBadData
        ? state.lastValidData[key].topOptions
        : updatesWithStrike;

      if (!hasRecentBadData) {
        state.lastValidData[key].topOptions = updatesWithStrike;
      }

      const updatedAdaptive = [
        ...(state.adaptiveTopOptions[key] || []),
        ...dataToUse,
      ];

      return {
        ...state,
        topOptions: { ...state.topOptions, [key]: dataToUse },
        adaptiveTopOptions: {
          ...state.adaptiveTopOptions,
          [key]: updatedAdaptive,
        },
        lastValidData: {
          ...state.lastValidData,
          [key]: { ...state.lastValidData[key] },
        },
      };
    });
  },

  updateDynamicLevels: (_indexIgnored, updates = []) => {
    const key = KEY;
    set((state) => {
      const recentExposures = (state.greekExposures[key] || []).slice(-4);
      const hasRecentBadData =
        recentExposures.some(
          (exp) =>
            exp.calls_total === 0 ||
            exp.puts_total === 0 ||
            exp.net_exposure === 0
        ) && (state.lastValidData?.[key]?.dynamicLevels?.length || 0) > 0;

      const dataToUse = hasRecentBadData
        ? state.lastValidData[key].dynamicLevels
        : updates;

      if (!hasRecentBadData) {
        state.lastValidData[key].dynamicLevels = updates;
      }

      const updatedAdaptive = [
        ...(state.adaptiveDynamicLevels[key] || []),
        ...dataToUse,
      ];

      return {
        ...state,
        dynamicLevels: { ...state.dynamicLevels, [key]: dataToUse },
        adaptiveDynamicLevels: {
          ...state.adaptiveDynamicLevels,
          [key]: updatedAdaptive,
        },
        lastValidData: {
          ...state.lastValidData,
          [key]: { ...state.lastValidData[key] },
        },
      };
    });
  },
});

const useGreeksWebsocketDataStore = create(createGreeksState);

export default useGreeksWebsocketDataStore;


























